import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useMemo } from "react";

import { SimulatorPickActionCell } from "@/components/Reports/SimulatorPickActionCell";
import { DataTable } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ConditionAnalysisBucketRow } from "../../../../utils/conditionAnalysisSelectors";
import { bucketMetricKeyFromBucket } from "../../../../utils/conditionAnalysisSelectors";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import { TradeLogSortableCell } from "../../TradeLog/components/tradeLogSortableCell";
import {
  AddToSimulatorColumnHeader,
  MarketContextActionColumnCell,
  MarketContextPreviewTradesButton,
  MarketContextPreviewTradesColumnHeader,
  MarketContextTooltipContent,
  fmtConfidence,
  fmtInt,
  fmtNum,
  fmtPct,
  fmtPnl,
  MarketContextMetricColumnHeader,
} from "./marketContextDataTableShared";
import { marketContextVariantTooltipText } from "./marketContextVariantTooltipText";
import { WIN_RATE_COLUMN_TOOLTIP } from "./marketContextTableTooltips";
export type MarketContextBucketTableProps = {
  rows: ConditionAnalysisBucketRow[];
  onPickBucket?: (row: ConditionAnalysisBucketRow, metricKey: string) => void;
  isPicked?: (row: ConditionAnalysisBucketRow, metricKey: string) => boolean;
  onPreviewBucket?: (row: ConditionAnalysisBucketRow) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  metricDefinitions?: Record<string, unknown>;
  /** Matches `bucket` column when deep-linking from findings. */
  highlightBucket?: string | null;
  evidenceFocusNonce?: number;
  tableVariant?: "best" | "worst";
  emptyMessage?: string;
};

function bucketPreviewRowKey(row: ConditionAnalysisBucketRow): string {
  return `bucket:${String(row.bucket ?? "")}:${String(row.segment_key ?? row.drill_key ?? "")}`;
}

function createBucketColumns(props: MarketContextBucketTableProps): ColumnDef<ConditionAnalysisBucketRow>[] {
  const showPickColumn = Boolean(props.onPickBucket);
  const showPreviewColumn = Boolean(props.onPreviewBucket);
  const tooltipLabelClass =
    "cursor-help text-sm font-medium text-gray-500 dark:text-sidebar-foreground font-[family-name:var(--font-primary)] normal-case underline decoration-dotted underline-offset-2";

  const cols: ColumnDef<ConditionAnalysisBucketRow>[] = [
    {
      id: "bucketType",
      accessorFn: (row) => String(row.bucket ?? ""),
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Bucket type" align="left" />
      ),
      cell: ({ row }) => {
        const r = row.original;
        const metricKey = bucketMetricKeyFromBucket(r.bucket);
        const [bucketMetricPrefixRaw, ...bucketTypeRest] = String(r.bucket ?? "").split(":");
        const bucketMetricPrefix = bucketMetricPrefixRaw || metricKey;
        const bucketTypeSuffix = bucketTypeRest.length ? `:${bucketTypeRest.join(":")}` : "";
        const bucketVariant = bucketTypeRest.join(":").trim();
        const bucketDisplayLabel = bucketTypeSuffix
          ? `${bucketMetricPrefix}${bucketTypeSuffix}`
          : bucketMetricPrefix;
        const tooltipText = bucketVariant
          ? marketContextVariantTooltipText(bucketMetricPrefix, bucketVariant, props.metricDefinitions)
          : "";

        return (
          <TradeLogSortableCell data-market-context-wrap-cell="" className="whitespace-normal break-words">
            <span className="inline-flex flex-wrap items-center gap-0 font-medium">
              {tooltipText ? (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={tooltipLabelClass} data-no-row-click="">
                        {bucketDisplayLabel}
                      </span>
                    </TooltipTrigger>
                    <MarketContextTooltipContent widthClass="max-w-[360px]">
                      {tooltipText}
                    </MarketContextTooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span>{bucketDisplayLabel}</span>
              )}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
  ];

  if (showPickColumn) {
    cols.push({
      id: "simulatorPick",
      enableSorting: false,
      header: () => <AddToSimulatorColumnHeader />,
      cell: ({ row }) => {
        const metric = bucketMetricKeyFromBucket(row.original.bucket);
        return (
          <MarketContextActionColumnCell>
            <div data-market-context-simulator-pick-cell="">
              <SimulatorPickActionCell
                picked={Boolean(props.isPicked?.(row.original, metric))}
                addAriaLabel="Add to Simulator"
                onAdd={() => props.onPickBucket?.(row.original, metric)}
              />
            </div>
          </MarketContextActionColumnCell>
        );
      },
    });
  }

  cols.push(
    {
      id: "sample",
      accessorFn: (row) => row.sample_size ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Sample"
          tooltip="All trades in this row, including scratch/breakeven."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtInt(row.original.sample_size)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "winRate",
      accessorFn: (row) => row.win_rate ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Win rate" tooltip={WIN_RATE_COLUMN_TOOLTIP} />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtPct(row.original.win_rate)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "expectancy",
      accessorFn: (row) => row.expectancy ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Expectancy"
          tooltip="Average expected return per trade for the bucket."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtNum(row.original.expectancy)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "totalPnl",
      accessorFn: (row) => row.total_pnl ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Total PnL"
          tooltip="Total profit and loss across all trades in the bucket."
        />
      ),
      cell: ({ row }) => {
        const totalPnl = row.original.total_pnl;
        if (totalPnl == null || !Number.isFinite(totalPnl)) {
          return (
            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(totalPnl) }}>
              {fmtPnl(totalPnl)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "confidence",
      accessorFn: (row) => row.confidence ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Confidence"
          tooltip="Backend confidence score for this bucket ranking."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtConfidence(row.original.confidence)}
        </TradeLogSortableCell>
      ),
    },
  );

  if (showPreviewColumn) {
    cols.push({
      id: "previewTrades",
      enableSorting: false,
      header: () => <MarketContextPreviewTradesColumnHeader />,
      cell: ({ row }) => {
        const rowKey = bucketPreviewRowKey(row.original);
        const disabled =
          typeof row.original.sample_size === "number" && row.original.sample_size <= 0;
        return (
          <MarketContextPreviewTradesButton
            rowKey={rowKey}
            activeKey={props.previewActiveKey}
            loadingKey={props.previewLoadingKey}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              props.onPreviewBucket?.(row.original);
            }}
          />
        );
      },
    });
  }

  return cols;
}

export function MarketContextBucketTable(props: MarketContextBucketTableProps): React.ReactElement {
  const columns = useMemo(
    () => createBucketColumns(props),
    [
      props.isPicked,
      props.metricDefinitions,
      props.onPickBucket,
      props.onPreviewBucket,
      props.previewActiveKey,
      props.previewLoadingKey,
    ],
  );

  const highlightRowId = useMemo(() => {
    const needle = props.highlightBucket?.trim();
    if (!needle) return undefined;
    const match = props.rows.find((r) => String(r.bucket ?? "").trim() === needle);
    return match ? String(match.bucket ?? "") : undefined;
  }, [props.highlightBucket, props.rows]);

  return (
    <div
      data-market-context-bucket-table=""
      data-market-context-bucket-variant={props.tableVariant ?? undefined}
      className="w-full min-w-0 rounded-[6px]"
    >
      <DataTable
        columns={columns}
        data={props.rows}
        getRowId={(row) => String(row.bucket ?? "")}
        activeRowId={highlightRowId}
        getRowDataAttributes={(row) => {
          const bucket = String(row.bucket ?? "").trim();
          const attrs: Record<string, string> = {};
          if (bucket) attrs["data-market-context-bucket"] = bucket;
          if (
            props.evidenceFocusNonce &&
            highlightRowId &&
            String(row.bucket ?? "") === highlightRowId
          ) {
            // Drive pulse via attribute only — do not remount the row (remount drops mid-click).
            attrs["data-market-context-evidence-highlight"] = String(props.evidenceFocusNonce);
          }
          return Object.keys(attrs).length ? attrs : undefined;
        }}
        showPagination={false}
        emptyMessage={
          props.emptyMessage ??
          (props.tableVariant === "worst"
            ? "No worst buckets available for this view."
            : props.tableVariant === "best"
              ? "No best buckets available for this view."
              : "No buckets available for this view.")
        }
        className="min-w-0"
      />
    </div>
  );
}
